"use client";

import React, { useEffect, useRef, useState } from "react";
import Image from "next/image";
import { Swiper as SwiperType } from "swiper";
import DynamicSwiper from "@/components/common/DynamicSwiper";
import { ProductDetail, Variant, Product } from "@/packages/types/products";
import RatingStars from "../common/RatingStar";
import Price from "../common/Price";
import SavePercentage from "../common/SavedPercentage";
import { useCart } from "@/packages/context/CartContext";
import { Button } from "../ui/button";
import QuantitySelector from "../common/QuantitySelector";
import { useFastRouter } from "@/packages/hooks/useFastRouter";

interface ProductShowcaseProps {
  product: ProductDetail;
  onAddedToCart?: (product: Product) => void;
  autoplay?: boolean;
}

const ProductShowcase: React.FC<ProductShowcaseProps> = ({
  product,
  onAddedToCart,
  autoplay = true,
}) => {
  const { cartItems, addToCart } = useCart();
  const { fastPush } = useFastRouter();
  const [quantity, setQuantity] = useState(1);
  const [selectedAttributes, setSelectedAttributes] = useState<
    Record<string, string>
  >({});
  const [selectedVariant, setSelectedVariant] = useState<Variant | null>(null);
  const [selectedImage, setSelectedImage] = useState<string | null>(null);
  const [isInCart, setIsInCart] = useState(false);

  const salePrice = selectedVariant?.salePrice ?? product?.salePrice;
  const comparePrice = selectedVariant?.comparePrice ?? product?.comparePrice;
  const hasDiscount = comparePrice && salePrice && comparePrice > salePrice;

  const mainSwiperRef = useRef<SwiperType | null>(null);
  const [mainAutoplay] = useState(autoplay);

  const attributeNames = Array.from(
    new Set(
      product?.variants.flatMap((v) =>
        v.variantAttributes.map((attr) => attr.attribute.name)
      )
    )
  );

  // Initialize default variant
  useEffect(() => {
    if (product && product.variants.length > 0) {
      const firstVariant = product.variants[0];
      setSelectedVariant(firstVariant);
      setSelectedImage(firstVariant.product_image?.thumbnailUrl || null);

      const initialAttrs: Record<string, string> = {};
      firstVariant.variantAttributes.forEach((attr) => {
        initialAttrs[attr.attribute.name] = attr.attribute_value.name;
      });
      setSelectedAttributes(initialAttrs);

      if (mainSwiperRef.current && product.images) {
        const index = product.images.findIndex(
          (img) => img.id === firstVariant.imageId
        );
        if (index !== -1) {
          mainSwiperRef.current.slideToLoop(index);
        }
      }
    }
  }, [product]);

  // Update image when variant changes
  useEffect(() => {
    if (selectedVariant && product?.images && mainSwiperRef.current) {
      const idx = product.images.findIndex(
        (img) => img.id === selectedVariant.imageId
      );
      if (idx !== -1) {
        mainSwiperRef.current.slideToLoop(idx);
        setSelectedImage(product.images[idx].thumbnailUrl);
      }
    }
  }, [selectedVariant, product]);

  // Check if already in cart
  useEffect(() => {
    if (!product) {
      setIsInCart(false);
      return;
    }
    const found = cartItems.some((item) => {
      const sameProduct = item.id === product.id;
      if (!selectedVariant) return sameProduct;
      return sameProduct && item.variant?.id === selectedVariant?.id;
    });
    setIsInCart(found);
  }, [cartItems, product, selectedVariant]);

  const getAttributeValues = (attrName: string) =>
    Array.from(
      new Set(
        product?.variants
          .flatMap((v) => v.variantAttributes)
          .filter((attr) => attr.attribute.name === attrName)
          .map((attr) => attr.attribute_value.name)
      )
    );

  const isValueValid = (attrName: string, value: string) => {
    const baseAttr = attributeNames[0];
    if (attrName === baseAttr) return true;

    return product?.variants.some((variant) => {
      const matchesBase = variant.variantAttributes.some(
        (attr) =>
          attr.attribute.name === baseAttr &&
          attr.attribute_value.name === selectedAttributes[baseAttr]
      );
      const matchesCurrent = variant.variantAttributes.some(
        (attr) =>
          attr.attribute.name === attrName &&
          attr.attribute_value.name === value
      );
      return matchesBase && matchesCurrent;
    });
  };

  const handleAttributeSelect = (attrName: string, value: string) => {
    const updated = { ...selectedAttributes, [attrName]: value };

    const matchedVariant = product?.variants.find((variant) =>
      variant.variantAttributes.every(
        (attr) => updated[attr.attribute.name] === attr.attribute_value.name
      )
    );

    if (matchedVariant) {
      setSelectedVariant(matchedVariant);
      setSelectedAttributes(updated);
      setSelectedImage(matchedVariant.product_image?.thumbnailUrl || null);
    } else {
      // Fallback: find any variant with the selected attribute value
      const fallback = product?.variants.find((variant) =>
        variant.variantAttributes.some(
          (attr) =>
            attr.attribute.name === attrName &&
            attr.attribute_value.name === value
        )
      );
      if (fallback) {
        const fallbackAttrs: Record<string, string> = {};
        fallback.variantAttributes.forEach((attr) => {
          fallbackAttrs[attr.attribute.name] = attr.attribute_value.name;
        });

        setSelectedVariant(fallback);
        setSelectedAttributes(fallbackAttrs);
        setSelectedImage(fallback.product_image?.thumbnailUrl || null);
      }
    }
  };

  const handleThumbnailClick = (idx: number, url: string) => {
    setSelectedImage(url);
    if (mainSwiperRef.current) {
      mainSwiperRef.current.slideToLoop(idx);
      // Stop autoplay when user manually selects thumbnail
      if (mainAutoplay) {
        mainSwiperRef.current.autoplay.stop();
      }
    }
  };

  const handleAddToCart = () => {
    if (!product) return;
    addToCart(product, quantity, selectedVariant || undefined);
    if (onAddedToCart) onAddedToCart(product);
  };

  return (
    <div className="overflow-x-hidden h-[500px] md:h-auto">
      <div className="w-full max-w-[90vw] md:max-w-5xl mx-auto p-4 md:p-6 flex flex-col md:flex-row gap-6 md:gap-8 overflow-hidden">
        {/* Left: Images */}
        <div className="w-full md:w-1/2">
          {mainAutoplay ? (
            <DynamicSwiper
              items={product?.images || []}
              slidesPerView={1}
              spaceBetween={10}
              navigation
              autoplay={mainAutoplay}
              pagination={false}
              renderItem={(img) => (
                <Image
                  src={img.imageUrl.trim() || "/assets/site/images/default.png"}
                  height={360}
                  width={420}
                  onError={(e) => {
                    e.currentTarget.onerror = null; // disables further error handling
                    e.currentTarget.srcset = "/assets/site/images/default.png";
                  }}
                  alt={product?.title || "Product"}
                  className="rounded-lg w-full max-h-[360px] sm:max-h-[420px] object-contain"
                />
              )}
              onSwiper={(swiper: SwiperType) =>
                (mainSwiperRef.current = swiper)
              }
            />
          ) : (
            <div className="w-full">
              <Image
                src={
                  selectedImage?.trim() ||
                  product?.images?.[0]?.imageUrl.trim() ||
                  "/assets/site/images/default.png"
                }
                onError={(e) => {
                  e.currentTarget.onerror = null; // disables further error handling
                  e.currentTarget.srcset = "/assets/site/images/default.png";
                }}
                height={360}
                width={420}
                alt={product?.title || "Product"}
                className="rounded-lg w-full max-h-[360px] sm:max-h-[420px] object-contain"
              />
            </div>
          )}

          {/* Thumbnails */}
          <div className="mt-3 overflow-x-auto">
            <DynamicSwiper
              items={product?.images || []}
              slidesPerView={4}
              spaceBetween={10}
              navigation={false}
              autoplay={false}
              pagination={false}
              renderItem={(img, idx) => (
                <Image
                  key={idx}
                  src={
                    img.thumbnailUrl?.trim() ||
                    "/assets/site/images/default.png"
                  }
                  onError={(e) => {
                    e.currentTarget.onerror = null; // disables further error handling
                    e.currentTarget.srcset = "/assets/site/images/default.png";
                  }}
                  height={70}
                  width={70}
                  alt={`Thumbnail ${idx + 1}`}
                  className={`cursor-pointer border rounded-lg p-1 min-w-[70px] hover:border-primary ${
                    selectedImage === img.thumbnailUrl
                      ? "border-blue-500"
                      : "border-gray-300"
                  }`}
                  onClick={() => handleThumbnailClick(idx, img.thumbnailUrl)}
                />
              )}
            />
          </div>
        </div>

        {/* Right: Info */}
        {/* Right: Info */}
        <div
          className="
    w-full md:w-1/2
    max-h-[50vh] md:max-h-none
    overflow-y-auto
    pr-2
    scrollbar-thin scrollbar-thumb-gray-300 hover:scrollbar-thumb-gray-400
  "
        >
          <h1 className="text-lg sm:text-xl font-semibold break-words">
            {product?.title}
          </h1>

          {product?.totalRating && (
            <div className="mt-2">
              <RatingStars
                rating={product.averageRating}
                reviewCount={product.totalRating}
              />
            </div>
          )}

          {/* Price */}
          <div className="flex items-center flex-wrap gap-x-4 mt-2">
            <Price
              amount={Number(salePrice)}
              className="text-primary text-lg"
            />
            {hasDiscount && (
              <>
                <Price
                  amount={Number(comparePrice)}
                  className="text-gray-500 text-sm line-through"
                />
                <SavePercentage
                  originalPrice={Number(comparePrice)}
                  discountedPrice={Number(salePrice)}
                />
              </>
            )}
          </div>

          {/* Attributes */}
          {attributeNames.map((attrName) => (
            <div key={attrName} className="mt-4">
              <p className="text-sm font-semibold mb-1">
                {attrName}:{" "}
                <span className="text-blue-600 ml-2">
                  {selectedAttributes[attrName]}
                </span>
              </p>
              <div className="flex flex-wrap gap-2">
                {getAttributeValues(attrName).map((value) => {
                  const isDisabled = !isValueValid(attrName, value);
                  const variant = product?.variants.find((v) =>
                    v.variantAttributes.some(
                      (a) =>
                        a.attribute.name === attrName &&
                        a.attribute_value.name === value
                    )
                  );
                  const outOfStock =
                    variant?.stock !== undefined && variant.stock <= 0;

                  const isActive = selectedAttributes[attrName] === value;
                  return (
                    <Button
                      key={value}
                      disabled={isDisabled || outOfStock}
                      onClick={() => handleAttributeSelect(attrName, value)}
                      className={`px-3 py-1 border rounded text-sm cursor-pointer ${
                        isActive
                          ? "border-[var(--brand-accent)] bg-[var(--brand-accent)]/10 text-[var(--brand-accent)] font-semibold"
                          : isDisabled || outOfStock
                          ? "border-gray-300 text-gray-400 cursor-not-allowed"
                          : "border-gray-300 hover:bg-gray-100"
                      }`}
                    >
                      {value}
                    </Button>
                  );
                })}
              </div>
            </div>
          ))}

          {/* Quantity + Add to Cart */}
          {(() => {
            const currentStock = selectedVariant ? Number(selectedVariant.stock ?? 0) : Number(product.stock ?? 0);
            return (
              <div className="flex flex-wrap items-center gap-3 mt-6 w-full sm:w-[300px]">
                {!isInCart && currentStock > 0 && (
                  <QuantitySelector
                    value={quantity}
                    min={1}
                    max={currentStock}
                    onChange={(newQty) => setQuantity(newQty)}
                    className="border border-gray-300 rounded-md"
                  />
                )}
                {isInCart && (
                  <>
                    <p className="text-sm text-gray-700">Item added to cart</p>
                    <Button
                      className="w-[100px] bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] text-white py-2 rounded text-sm cursor-pointer"
                      onClick={() => fastPush(`/cart`)}
                    >
                      View Cart
                    </Button>
                  </>
                )}
                {!isInCart && (
                  <Button
                    onClick={handleAddToCart}
                    disabled={currentStock <= 0}
                    className={`px-6 py-2 rounded-md text-white ${
                      currentStock <= 0
                        ? "bg-slate-200 text-slate-500 cursor-not-allowed border border-slate-300"
                        : "bg-[var(--brand-accent)] hover:bg-[var(--brand-accent-hover)] cursor-pointer"
                    }`}
                  >
                    {currentStock <= 0 ? "Out of Stock" : "Add to Cart"}
                  </Button>
                )}
              </div>
            );
          })()}
        </div>
      </div>
    </div>
  );
};

export default ProductShowcase;
